<template>
  <component
    :is="tag"
    ref="_ref"
    v-bind="_props"
    :class="[
      ns.b(),
      ns.m(_type),
      ns.m(_size),
      ns.is('disabled', _disabled),
      ns.is('loading', loading),
      ns.is('plain', plain),
      ns.is('ghost', ghost),
      ns.is('round', round),
      ns.is('circle', circle),
      ns.is('text', text),
      ns.is('link', link),
      ns.is('has-bg', bg),
      { 'is-dashed is-plain': dashed },
      ns.is('square', square),
      ns.is('icon-only', !!iconOnly),
      ns.is('gradient', !!props.gradientColor),
      wrap ? ns.m('wrap') : '',
      isWrap ? ns.m('is-wrap') : '',
    ]"
    :style="buttonStyle"
    data-component-name="Button"
    data-event-name="click"
    :data-event-content="childrenText"
    :data-component-label="childrenText"
    @click="handleClick"
  >
    <template v-if="loading">
      <slot v-if="$slots.loading" name="loading" />
      <jd-icon v-else :class="ns.is('loading')">
        <component :is="loadingIcon" />
      </jd-icon>
    </template>
    <jd-icon v-else-if="icon || $slots.icon">
      <component :is="icon" v-if="icon" />
      <slot v-else name="icon" />
    </jd-icon>
    <span
      ref="wrapTextWrapper"
      v-if="$slots.default"
      :class="{ [ns.em('text', 'expand')]: shouldAddSpace }"
    >
      <slot />
    </span>
  </component>
</template>

<script lang="ts" setup>
import { computed, useSlots } from 'vue'
import { JdIcon } from '@jdesign-vue/components/icon'
import { useDeprecated, useNamespace } from '@jdesign-vue/hooks'
import { useButton } from './use-button'
import { buttonEmits, buttonProps } from './button'
import { useButtonCustomStyle } from './button-custom'

defineOptions({
  name: 'JdButton',
})

const props = defineProps(buttonProps)
const emit = defineEmits(buttonEmits)
const slots = useSlots()
const buttonStyle = useButtonCustomStyle(props)
const ns = useNamespace('button')

useDeprecated(
  {
    from: 'gradient-color / gradientColor',
    replacement: 'type="gradient-ai"',
    scope: 'JdButton',
    version: '3.0.0',
    ref: 'https://jdesign.jd.com/vue/component/basic/button',
    type: 'Attribute',
  },
  computed(() => !!props.gradientColor)
)
const {
  _ref,
  _size,
  _type,
  _disabled,
  _props,
  shouldAddSpace,
  handleClick,
  wrapTextWrapper,
  isWrap,
} = useButton(props, emit)

const iconOnly = computed(
  () =>
    (props.icon || slots.icon) &&
    !slots.default &&
    !props.round &&
    !props.circle
)

const getTextFromChildren = (node: any): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getTextFromChildren).join('')

  const children = node.children
  if (children === null || children === undefined || typeof children === 'boolean') return ''
  if (typeof children === 'string' || typeof children === 'number') return String(children)
  if (Array.isArray(children)) return children.map(getTextFromChildren).join('')
  return ''
}

const childrenText = computed(() => {
  const vnodes = slots.default?.() ?? []
  return getTextFromChildren(vnodes).trim()
})

defineExpose({
  /** @description button html element */
  ref: _ref,
  /** @description button size */
  size: _size,
  /** @description button type */
  type: _type,
  /** @description button disabled */
  disabled: _disabled,
  /** @description whether adding space */
  shouldAddSpace,
})
</script>
